import React, { useState, useEffect } from "react";
import axios from "axios";
import SEO from '../SEO/SEO';
import { motion } from "framer-motion";
import { ChevronRight, Star, Building } from "lucide-react";
import { Link } from 'react-router-dom';
import {
  HomeIcon,
  BuildingOffice2Icon,
  StarIcon as HeroStarIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  ClockIcon,
  DocumentCheckIcon,
  CheckCircleIcon,
  SparklesIcon,
  BoltIcon,
  HeartIcon,
  LightBulbIcon,
  RocketLaunchIcon,
  ChartBarIcon,
  UserGroupIcon,
  BanknotesIcon,
  KeyIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

// Icon mapping to match admin
const iconMap = {
  HomeIcon,
  BuildingOffice2Icon,
  StarIcon: HeroStarIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  ClockIcon,
  DocumentCheckIcon,
  CheckCircleIcon,
  SparklesIcon,
  BoltIcon,
  HeartIcon,
  LightBulbIcon,
  RocketLaunchIcon,
  ChartBarIcon,
  UserGroupIcon,
  BanknotesIcon,
  KeyIcon
};

const AboutUs = () => {

  const youtubeLink = "https://www.youtube.com/@ahamhfc";

  // Videos State
  const [journeyVideos, setJourneyVideos] = useState([]);
  const [journeySettings, setJourneySettings] = useState({
    section_title: "Watch Our Journey",
    section_description: "See how we help families build their dream homes.",
    is_active: true
  });


  const [banner, setBanner] = useState(null);
  const [overview, setOverview] = useState(null);
  const [visionMission, setVisionMission] = useState(null);
  const [coreValues, setCoreValues] = useState([]);
  const [coreValuesSettings, setCoreValuesSettings] = useState({
    section_title: "Our Core Values",
    section_subtitle: "The foundation of our culture — guiding every step we take toward excellence."
  });

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const response = await axios.get('/api/admin/banners/active?page_type=about_aham');
        if (response.data.success && response.data.data.length > 0) {
          setBanner(response.data.data[0]);
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
      }
    };

    const fetchOverview = async () => {
      try {
        const response = await axios.get('/api/admin/about-aham/section/overview');
        if (response.data.success && response.data.data) {
          const data = response.data.data;
          data.description = typeof data.description === 'string' ? JSON.parse(data.description) : data.description;
          data.content_data = typeof data.content_data === 'string' ? JSON.parse(data.content_data) : data.content_data;
          setOverview(data);
        }
      } catch (error) {
        console.error('Error fetching overview:', error);
      }
    };

    const fetchVisionMission = async () => {
      try {
        const response = await axios.get('/api/admin/about-aham/section/vision_mission');
        if (response.data.success && response.data.data) {
          const data = response.data.data;
          data.content_data = typeof data.content_data === 'string' ? JSON.parse(data.content_data) : data.content_data;
          setVisionMission(data);
        }
      } catch (error) {
        console.error('Error fetching vision & mission:', error);
      }
    };

    const fetchCoreValues = async () => {
      try {
        const [valuesRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/key-benefits/about_us_core_values/active'),
          axios.get('/api/admin/key-benefits/settings/about_us_core_values')
        ]);

        if (valuesRes.data.success) {
          setCoreValues(valuesRes.data.data);
        }

        if (settingsRes.data.success && settingsRes.data.data) {
          setCoreValuesSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching core values:', error);
      }
    };

    fetchBanner();
    fetchOverview();
    fetchVisionMission();
    fetchCoreValues();

    const fetchJourneyDatas = async () => {
      try {
        const [videosRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/about-aham/videos/active'),
          axios.get('/api/admin/about-aham/videos/settings')
        ]);

        if (videosRes.data.success) {
          setJourneyVideos(videosRes.data.data);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setJourneySettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching journey data:', error);
      }
    };
    fetchJourneyDatas();
  }, []);
  
  
  // Schema for SEO
  const aboutSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://ahamhfc.com/#organization",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "legalName": "AHAM HOUSING FINANCE LIMITED",
        "alternateName": "Aham Housing Finance Private Limited",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "image": "https://ahamhfc.com/img/about-aham/Business-analytics-1769664641881-105831392.webp",
        "description": "AHAM HOUSING FINANCE LIMITED is dedicated to fulfilling every individual's housing dream and aims to be a trusted brand recognized for reliability.",
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "New No. 2 & 4, Old No. 24 & 25, 3rd Floor, Sivaganga Road, Nungambakkam",
          "addressLocality": "Chennai",
          "addressRegion": "Tamil Nadu",
       "postalCode": "600034",
          "addressCountry": "IN"
        },
        "sameAs": [
          "https://www.facebook.com/ahamhfc",
          "https://www.instagram.com/aham_housingfinance/",
          "https://linkedin.com/company/ahamhfc",
          "https://www.youtube.com/@ahamhfc"
        ]
      },
      {
        "@type": "FinancialService",
        "@id": "https://ahamhfc.com/#financialservice",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "url": "https://ahamhfc.com/",
        "image": "https://ahamhfc.com/img/about-aham/Business-analytics-1769664641881-105831392.webp",
        "description": "AHAM HOUSING FINANCE LIMITED offers a wide range of home loans across various segments and diverse regions with attractive interest rates and minimum documentation or no-income-proof basis.",
        "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "New No. 2 & 4, Old No. 24 & 25, 3rd Floor, Sivaganga Road, Nungambakkam",
          "addressLocality": "Chennai",
          "addressRegion": "Tamil Nadu",
          "postalCode": "600034",
          "addressCountry": "IN"
        }
      },
      {
        "@type": "WebPage",
        "@id": "https://ahamhfc.com/about-aham-housing-finance/#webpage",
        "url": "https://ahamhfc.com/about-aham-housing-finance",
        "name": "About Aham Housing Finance",
        "description": "Learn about AHAM HOUSING FINANCE LIMITED, its mission, trust-focused approach, and commitment to helping customers fulfill their housing dream.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "contentUrl": "https://ahamhfc.com/img/about-aham/Business-analytics-1769664641881-105831392.webp"
        },
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        }
      },
      {
        "@type": "AboutPage",
        "@id": "https://ahamhfc.com/about-aham-housing-finance/#aboutpage",
        "url": "https://ahamhfc.com/about-aham-housing-finance",
        "name": "About Aham Housing Finance",
        "mainEntity": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "about": {
          "@id": "https://ahamhfc.com/#financialservice"
        },
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://ahamhfc.com/about-aham-housing-finance/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://ahamhfc.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "About Us",
            "item": "https://ahamhfc.com/about-aham-housing-finance"
          }
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://ahamhfc.com/#website",
        "url": "https://ahamhfc.com/",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "inLanguage": "en-IN"
      }
    ]
  };
  

  return (
    <div className="bg-white text-gray-800 overflow-hidden">

      <SEO
        title="About Aham Housing Finance | No Income Proof Home Loans "
        description="Discover how Aham Housing Finance helps families achieve homeownership with affordable loans, simple processes, and fast approvals."
        keywords="Aham Housing Finance about, about Aham Housing Finance, Aham Housing Finance company profile, affordable home loan company, home loans without income proof, customer centric housing finance, transparent home loan solutions, trusted housing finance company, home ownership dreams India, housing finance company India, low documentation home loans, self employed home loan lender, informal income home loans, first time home buyer finance, reliable home loan provider, fast home loan approval, Aham Housing Finance mission, Aham Housing Finance vision, housing finance for underserved customers, Aham company overview"
        canonical="https://ahamhfc.com/about-aham-housing-finance"
        ogTitle="About Aham Housing Finance | No Income Proof Home Loans"
        ogDescription="Discover how Aham Housing Finance helps self-employed and informal income families get affordable home loans without income proof and fast, transparent approvals."
       schema={aboutSchema}
    />
      {/* ===== Hero Banner Section ===== */}
      <section className="relative min-h-[60vh] flex items-center justify-start overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${banner?.image_url ? (banner.image_url.startsWith('http') ? banner.image_url : `/${banner.image_url}`) : 'img/Banners/About-Banner.webp'}')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
              {banner?.title || "About Us"}
            </h1>
            <p className="text-lg md:text-3xl text-white/90 font-medium max-w-4xl leading-relaxed mb-6 md:mb-8">
              {banner?.subtitle || "Building Dreams. Empowering Lives."}
            </p>

            {/* Breadcrumb Navigation */}
            <div className="flex items-center space-x-2">
              <Link
                to="/"
                className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
              >
                Home
              </Link>
              <ChevronRightIcon className="h-4 w-4 text-white/60" />
              <span className="text-yellow-300 text-sm font-semibold">About Us</span>
            </div>
          </motion.div>
        </div>
      </section>


      {/* ===== About Company Section ===== */}
      <section className="relative py-20 px-6 md:px-12 lg:px-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="text-center md:text-left text-3xl md:text-4xl font-bold text-blue-800 leading-tight">
              {overview?.title ? (
                <>
                  {overview.title.split(' ').slice(0, 1).join(' ')}{" "}
                  <span className="text-blue-600">
                    {overview.title.split(' ').slice(1).join(' ')}
                  </span>
                </>
              ) : (
                <>
                  About{" "}
                  <span className="text-blue-600">
                    Aham Housing Finance Limited
                  </span>
                </>
              )}
            </h2>
            {overview?.description ? (
              overview.description.map((para, index) => (
                <p key={index} className="text-gray-700 leading-relaxed text-center md:text-left" dangerouslySetInnerHTML={{ __html: para }} />
              ))
            ) : (
              <div className="text-center md:text-left">
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Aham Housing Finance Limited</strong> is dedicated to
                  fulfilling every individual’s housing dream. We aim to be a
                  trusted brand that’s recognized for reliability, transparency, and
                  customer-first service.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our reach extends across Tier II and Tier III cities — where we
                  empower families and individuals to achieve financial independence
                  through accessible housing solutions.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Through our{" "}
                  <span className="font-semibold text-blue-700">
                    interview-based assessment
                  </span>{" "}
                  and proprietary scoring model, we make fair and informed lending
                  decisions for every customer.
                </p>
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative group"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-blue-700 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-500"></div>
            <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden">
              <img
                src={overview?.image_url ? (overview.image_url.startsWith('http') ? overview.image_url : `/${overview.image_url}`) : "img/ahamMoments/Dummy/Business-analytics.webp"}
                alt="Aham Housing Finance advisor helping a self-employed family in a Tier 2 city get a no income proof home loan for their first home."
                className="w-full h-64 object-cover"
              />
              <div className="p-6 space-y-3 text-center md:text-left">
                <h3 className="text-xl font-semibold text-blue-800">
                  {overview?.content_data?.card_title || "Empowering Home Dreams"}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {overview?.content_data?.card_description || "Simplifying home finance for those with limited access to formal income proofs — ensuring everyone has a fair chance to own their dream home."}
                </p>
                <a
                  href={overview?.content_data?.button_link || "https://aham.mbwhost.in/contact"}
                  className="mt-4 px-5 py-2 bg-[#ed2636] text-white rounded-lg shadow hover:bg-blue-700 transition inline-block"
                >
                  {overview?.content_data?.button_text || "Contact Us"}
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== Vision & Mission (Modern Dual Layout) ===== */}
      {visionMission?.is_active !== false && (visionMission?.content_data?.vision?.is_active !== false || visionMission?.content_data?.mission?.is_active !== false) && (
        <section className="relative py-24 bg-white overflow-hidden">
          {/* Decorative Gradient Backgrounds */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-[700px] h-[700px] bg-blue-100 rounded-full blur-3xl opacity-30 animate-pulse"></div>
            <div className="absolute bottom-0 right-0 w-[700px] h-[700px] bg-blue-200 rounded-full blur-3xl opacity-30 animate-pulse"></div>
          </div>

          <div className="relative max-w-6xl mx-auto text-center mb-16 px-6">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold text-blue-800"
            >
              {visionMission?.title || "Our Vision & Mission"}
            </motion.h2>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
              {visionMission?.description || "Driven by compassion, guided by integrity — we build homes and hopes."}
            </p>
          </div>

          {/* Vision and Mission Blocks */}
          <div className={`relative grid ${visionMission?.content_data?.vision?.is_active !== false && visionMission?.content_data?.mission?.is_active !== false ? 'md:grid-cols-2' : 'grid-cols-1 max-w-2xl mx-auto'} gap-8 md:gap-10 max-w-6xl mx-auto px-4 md:px-12`}>
            {/* Vision Section */}
            {visionMission?.content_data?.vision?.is_active !== false && (
              <motion.div
                initial={typeof window !== 'undefined' && window.innerWidth < 768 ? false : { opacity: 0, y: 20 }}
                whileInView={typeof window !== 'undefined' && window.innerWidth < 768 ? false : { opacity: 1, y: 0 }}
                transition={{ duration: 0.9 }}
                viewport={{ once: true }}
                className="group relative rounded-3xl overflow-hidden bg-gradient-to-br from-blue-700 to-blue-500 text-white shadow-xl hover:shadow-2xl transition-all duration-500 max-w-md md:max-w-none mx-auto w-full"
              >
                <div className="absolute inset-0  bg-cover bg-center opacity-20 group-hover:opacity-30 transition duration-500"></div>
                <div className="relative p-6 md:p-10 space-y-6 text-center md:text-left flex flex-col items-center md:items-start">
                  <div className="flex flex-col md:flex-row items-center md:items-start gap-4">
                    <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                      {visionMission?.content_data?.vision?.icon === 'Star' ? <Star className="w-8 h-8 text-yellow-300" /> :
                        visionMission?.content_data?.vision?.icon === 'Building' ? <Building className="w-8 h-8 text-yellow-300" /> :
                          <Star className="w-8 h-8 text-yellow-300" />}
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold tracking-wide">
                      {visionMission?.content_data?.vision?.title || "Our Vision"}
                    </h3>
                  </div>
                  <p className="text-blue-50 text-lg leading-relaxed">
                    {visionMission?.content_data?.vision?.content || "To be India’s most trusted housing finance partner — enabling every aspiring homeowner to achieve their dream with dignity and transparency. We aim to redefine housing access through compassion, innovation, and trust."}
                  </p>
                  <div className="h-1 w-1/3 bg-white/40 rounded-full mt-4 mx-auto md:mx-0"></div>
                </div>
              </motion.div>
            )}

            {/* Mission Section */}
            {visionMission?.content_data?.mission?.is_active !== false && (
              <motion.div
                initial={typeof window !== 'undefined' && window.innerWidth < 768 ? false : { opacity: 0, y: 20 }}
                whileInView={typeof window !== 'undefined' && window.innerWidth < 768 ? false : { opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.2 }}
                viewport={{ once: true }}
                className="group relative rounded-3xl overflow-hidden bg-gradient-to-tr from-blue-100 to-blue-50 text-blue-900 shadow-xl hover:shadow-2xl transition-all duration-500 max-w-md md:max-w-none mx-auto w-full"
              >
                <div className="absolute inset-0  bg-cover bg-center opacity-10 group-hover:opacity-20 transition duration-500"></div>
                <div className="relative p-6 md:p-10 space-y-6 text-center md:text-left flex flex-col items-center md:items-start">
                  <div className="flex flex-col md:flex-row items-center md:items-start gap-4">
                    <div className="p-4 bg-blue-200 rounded-2xl">
                      {visionMission?.content_data?.mission?.icon === 'Building' ? <Building className="w-8 h-8 text-blue-700" /> :
                        visionMission?.content_data?.mission?.icon === 'Star' ? <Star className="w-8 h-8 text-blue-700" /> :
                          <Building className="w-8 h-8 text-blue-700" />}
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold tracking-wide">
                      {visionMission?.content_data?.mission?.title || "Our Mission"}
                    </h3>
                  </div>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    {visionMission?.content_data?.mission?.content || "To empower every Indian family with fair, accessible, and transparent financial solutions that make home ownership possible. We’re committed to building stronger communities — one home at a time."}
                  </p>
                  <div className="h-1 w-1/3 bg-blue-300 rounded-full mt-4 mx-auto md:mx-0"></div>
                </div>
              </motion.div>
            )}
          </div>
        </section>
      )}


      {/* ===== Core Values Section ===== */}
      <section className="relative bg-white py-24 text-gray-800 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-center mb-4 text-[#2d347c]"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {coreValuesSettings.section_title}
          </motion.h2>
          <p className="text-center text-gray-600 text-lg mb-12 max-w-3xl mx-auto">
            {coreValuesSettings.section_subtitle}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {(coreValues.length > 0 ? coreValues : [
              {
                title: "Integrity",
                description: "We act with honesty, transparency, and fairness in everything we do.",
                icon_name: "💎",
                gradient_from: "primary-600",
                gradient_to: "primary-700"
              },
              {
                title: "Innovation",
                description: "We encourage creativity and adopt new ideas that drive growth.",
                icon_name: "⚡",
                gradient_from: "primary-600",
                gradient_to: "primary-700"
              },
              {
                title: "Collaboration",
                description: "We believe in teamwork that empowers and achieves shared success.",
                icon_name: "🤝",
                gradient_from: "primary-600",
                gradient_to: "primary-700"
              },
              {
                title: "Excellence",
                description: "We strive to deliver outstanding results and continuous improvement.",
                icon_name: "🏆",
                gradient_from: "primary-600",
                gradient_to: "primary-700"
              },
            ]).map((value, index) => {
              const IconComponent = iconMap[value.icon_name];
              const fromClass = `from-${value.gradient_from || 'primary-600'}`;
              const toClass = `to-${value.gradient_to || 'primary-700'}`;

              return (
                <motion.div
                  key={index}
                  className={`relative bg-gradient-to-br ${fromClass} ${toClass} rounded-2xl p-10 text-center shadow-md hover:shadow-xl transition-transform transform hover:-translate-y-2`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                >
                  <div className="flex flex-col items-center space-y-4">
                    <div className="text-5xl mb-2 opacity-90">
                      {IconComponent ? (
                        <IconComponent className="w-12 h-12 text-white" />
                      ) : (
                        value.icon_name
                      )}
                    </div>
                    <h3 className="text-xl font-semibold text-white">{value.title}</h3>
                    <p className="text-gray-100 text-sm leading-relaxed">
                      {value.description || value.desc}
                    </p>
                  </div>

                  {/* Soft inner overlay for balance */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-black/10 to-transparent opacity-60"></div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Gentle background texture */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(45,52,124,0.05),transparent_70%)] pointer-events-none"></div>
      </section>






      {/* ===== YouTube Section ===== */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-20 px-6 md:px-12 lg:px-20">
        {journeySettings.is_active && (
          <div className="max-w-6xl mx-auto text-center">

            <h2 className="text-3xl md:text-4xl font-bold text-blue-800 mb-10">
              {journeySettings.section_title}
            </h2>

            {/* AUTO SCROLL WRAPPER */}
            {journeyVideos.length > 0 ? (
              <div className="overflow-hidden py-4">
                <motion.div
                  className="flex gap-6"
                  animate={{ x: ["0%", "-100%"] }}
                  transition={{
                    repeat: Infinity,
                    duration: 30, // Slower for better view
                    ease: "linear"
                  }}
                >
                  {/* Duplicate items to create infinite loop - ensure we have enough items */}
                  {[...journeyVideos, ...journeyVideos, ...journeyVideos].map((video, index) => (
                    <div
                      key={index}
                      onClick={() => window.open(video.video_url, "_blank")}
                      className="min-w-[350px] md:min-w-[420px] cursor-pointer group"
                    >
                      <div className="relative rounded-xl overflow-hidden shadow-xl ">
                        <img
                          src={video.thumbnail_url ? (video.thumbnail_url.startsWith('http') ? video.thumbnail_url : `/${video.thumbnail_url}`) : "https://img.youtube.com/vi/placeholder/maxresdefault.jpg"}
                          alt={video.title || "Aham Video"}
                          className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
                          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                            <ChevronRight className="w-8 h-8 text-white ml-1" />
                          </div>
                        </div>

                        <div className="absolute bottom-4 right-4 bg-white text-blue-900 px-4 py-2 rounded-full flex items-center gap-2 shadow-lg text-sm font-semibold">
                          Watch Video <ChevronRight size={16} />
                        </div>
                      </div>
                      <h3 className="mt-4 text-left font-semibold text-gray-800 px-1 line-clamp-1">{video.title}</h3>
                    </div>
                  ))}
                </motion.div>
              </div>
            ) : (
              <p className="text-gray-500">No videos available at the moment.</p>
            )}

          </div>
        )}
      </section>
    </div>
  );
};

export default AboutUs;
